// Storage keys are wire commitments to existing browsers — pre-v3
// tabs may still be open with `agentfly_api_key` set, so we keep
// reading it as a one-shot legacy-login source.
export const SESSION_STORAGE = "agentfly_session";
export const SESSION_REMEMBER_KEY = "agentfly_session_remember";
export const LEGACY_API_KEY = "agentfly_api_key"; // pre-v3 only
export const ACCOUNT_CACHE_KEY = "agentfly_account_cache";
export const ACCOUNT_CACHE_TTL_MS = 5 * 60 * 1000;
export const SITE_THEME_KEY = "agentfly_site_theme";
export const CONSOLE_THEME_KEY = "agentfly_console_theme";

export function readStoredSession(): string {
  try {
    return (
      window.localStorage.getItem(SESSION_STORAGE) ||
      window.sessionStorage.getItem(SESSION_STORAGE) ||
      ""
    );
  } catch {
    return "";
  }
}

export function saveSession(token: string, remember: boolean): void {
  try {
    window.localStorage.removeItem(SESSION_STORAGE);
    window.sessionStorage.removeItem(SESSION_STORAGE);
    if (!token) return;
    (remember ? window.localStorage : window.sessionStorage).setItem(SESSION_STORAGE, token);
    window.localStorage.setItem(SESSION_REMEMBER_KEY, remember ? "1" : "0");
  } catch {
    // private mode etc.
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(SESSION_STORAGE);
    window.sessionStorage.removeItem(SESSION_STORAGE);
    window.localStorage.removeItem(SESSION_REMEMBER_KEY);
  } catch {
    // ignore
  }
}

export function readLegacyAPIKey(): string {
  try {
    return (
      window.localStorage.getItem(LEGACY_API_KEY) ||
      window.sessionStorage.getItem(LEGACY_API_KEY) ||
      ""
    );
  } catch {
    return "";
  }
}

export function clearLegacyAPIKey(): void {
  try {
    window.localStorage.removeItem(LEGACY_API_KEY);
    window.sessionStorage.removeItem(LEGACY_API_KEY);
  } catch {
    // ignore
  }
}

export interface AccountCacheEntry {
  api_key: string;
  cached_at: number;
  account: unknown;
}

export function readCachedAccount<T = unknown>(apiKey: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(ACCOUNT_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as AccountCacheEntry;
    if (!cached || cached.api_key !== apiKey || !cached.account) return null;
    if (Date.now() - Number(cached.cached_at || 0) > ACCOUNT_CACHE_TTL_MS) return null;
    return cached.account as T;
  } catch {
    return null;
  }
}

export function cacheAccount(apiKey: string, account: unknown): void {
  try {
    window.sessionStorage.setItem(
      ACCOUNT_CACHE_KEY,
      JSON.stringify({ api_key: apiKey, cached_at: Date.now(), account }),
    );
  } catch {
    // ignore
  }
}

export function clearAccountCache(): void {
  try {
    window.sessionStorage.removeItem(ACCOUNT_CACHE_KEY);
  } catch {
    // ignore
  }
}

// --- legacy compatibility shims (used by older v2 components that still reference saveAPIKey/clearAPIKey) ---
export function saveAPIKey(_apiKey: string, _remember: boolean): void {
  // noop: v3 uses session tokens; legacy callers are being phased out.
}

export function clearAPIKey(): void {
  clearLegacyAPIKey();
}

export function readStoredAPIKey(): string {
  return readLegacyAPIKey();
}
